import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, MapPin, X, Loader, ArrowUp, Users, Clock, Music2, Edit3 } from 'lucide-react';

const API_BASE = 'https://api.musicalmeet.com/api';

// ─── Venue Card ──────────────────────────────────────────────────────────────

function VenueCard({ venue, navigate }) {
  const priceRange = (() => {
    if (!venue.rooms || venue.rooms.length === 0) return null;
    const prices = venue.rooms.filter(r => r.pricePerHour).map(r => Number(r.pricePerHour));
    if (!prices.length) return null;
    const min = Math.min(...prices);
    const max = Math.max(...prices);
    return min === max ? `₹${min}/hr` : `₹${min}–₹${max}/hr`;
  })();

  return (
    <div
      style={{
        background: 'rgba(255,255,255,0.05)',
        border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: 16,
        overflow: 'hidden',
        transition: 'transform 0.25s ease, box-shadow 0.25s ease',
        cursor: 'default',
        height: '100%',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.transform = 'translateY(-5px)';
        e.currentTarget.style.boxShadow = '0 16px 40px rgba(99,102,241,0.3)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      {/* Cover image / placeholder */}
      <div style={{
        height: 145,
        background: 'linear-gradient(135deg,#1E1B4B,#4338CA)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        position: 'relative', overflow: 'hidden',
      }}>
        {venue.imageUrl ? (
          <img
            src={venue.imageUrl}
            alt={venue.venueName}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            onError={e => { e.target.style.display = 'none'; }}
          />
        ) : (
          <Music2 size={44} style={{ color: '#c4b5fd', opacity: 0.6 }} />
        )}
        {venue.venueType && (
          <span style={{
            position: 'absolute', top: 10, left: 10,
            background: 'rgba(67,56,202,0.88)', color: '#c4b5fd',
            borderRadius: 20, padding: '3px 11px', fontSize: '0.72rem',
            fontFamily: 'Gilroy-semiBold,sans-serif', backdropFilter: 'blur(6px)',
          }}>
            {venue.venueType}
          </span>
        )}
        {venue.instantBooking && (
          <span style={{
            position: 'absolute', top: 10, right: 10,
            background: 'rgba(16,185,129,0.88)', color: '#fff',
            borderRadius: 20, padding: '3px 10px', fontSize: '0.7rem',
            fontFamily: 'Gilroy-semiBold,sans-serif',
          }}>
            Instant Book
          </span>
        )}
      </div>

      <div style={{ padding: '14px 16px' }}>
        <h3 style={{
          fontFamily: 'Gilroy-semiBold,sans-serif', fontSize: '1rem',
          color: '#fff', margin: '0 0 6px', whiteSpace: 'nowrap',
          overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {venue.venueName || 'Unnamed Venue'}
        </h3>

        {(venue.city || venue.state) && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#9ca3af', fontSize: '0.82rem', marginBottom: 10 }}>
            <MapPin size={12} />
            {[venue.city, venue.state].filter(Boolean).join(', ')}
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
          {venue.rooms && venue.rooms.length > 0 && (
            <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#a5b4fc', fontSize: '0.78rem' }}>
              <Music2 size={12} />
              {venue.rooms.length} room{venue.rooms.length !== 1 ? 's' : ''}
            </span>
          )}
          {venue.capacity && (
            <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#a5b4fc', fontSize: '0.78rem' }}>
              <Users size={12} /> Up to {venue.capacity}
            </span>
          )}
          {venue.openingHours && (
            <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#a5b4fc', fontSize: '0.78rem' }}>
              <Clock size={12} /> {venue.openingHours}
            </span>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 12, gap: 8 }}>
          {priceRange && (
            <div style={{ color: '#10b981', fontFamily: 'Gilroy-semiBold,sans-serif', fontSize: '0.9rem', fontWeight: 600 }}>
              {priceRange}
            </div>
          )}
          <button
            onClick={() => navigate(`/public-venue/${venue._id}`)}
            style={{
              background: '#374EA9', border: 'none', borderRadius: 8, color: '#fff',
              padding: '8px 20px', cursor: 'pointer', fontFamily: "'Poppins', sans-serif",
              fontSize: '0.9rem', fontWeight: 600, whiteSpace: 'nowrap',
              boxShadow: '0 4px 16px rgba(55,78,169,0.3)', transition: 'background 0.2s',
              marginLeft: priceRange ? 'auto' : undefined,
            }}
            onMouseEnter={e => e.currentTarget.style.background = '#4a62d6'}
            onMouseLeave={e => e.currentTarget.style.background = '#374EA9'}
          >
            View Venue
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

const FeaturedVenues = () => {
  const navigate = useNavigate();
  const [venues, setVenues] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showArrow, setShowArrow] = useState(false);
  const [showScrollButtons, setShowScrollButtons] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [showLocationDialog, setShowLocationDialog] = useState(false);
  const [manualCity, setManualCity] = useState('');
  const [isRequestingLocation, setIsRequestingLocation] = useState(false);
  const [locationError, setLocationError] = useState(null);
  const [locationPermission, setLocationPermission] = useState('prompt');
  const [hasTriedAutoLocation, setHasTriedAutoLocation] = useState(false);
  const scrollContainerRef = useRef(null);

  // ── Fetch ──────────────────────────────────────────────────────────────────

  const fetchVenues = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/venues`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const allVenues = Array.isArray(data) ? data : [];

      if (userLocation?.city && !userLocation?.latitude) {
        // Filter by city name (manual / IP fallback)
        const cityLower = userLocation.city.toLowerCase();
        const filtered = allVenues.filter(v => v.city && v.city.toLowerCase().includes(cityLower));
        setVenues(filtered.length > 0 ? filtered : allVenues);
      } else {
        setVenues(allVenues);
      }
    } catch (err) {
      console.error('FeaturedVenues fetch error:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // ── Geolocation ────────────────────────────────────────────────────────────

  const requestLocationPermission = async (isAutoRequest = false) => {
    if (!navigator.geolocation) {
      if (!isAutoRequest) { setLocationError('Geolocation not supported'); setShowLocationDialog(true); }
      else fetchVenues();
      return;
    }
    setLocationError(null);
    setIsRequestingLocation(true);

    const tryGetPosition = (opts) => new Promise((resolve, reject) => {
      let done = false;
      const timer = setTimeout(() => { if (!done) { done = true; reject(new Error('TIMEOUT')); } }, opts._customTimeout || 12000);
      navigator.geolocation.getCurrentPosition(
        pos => { if (done) return; done = true; clearTimeout(timer); resolve(pos); },
        err => { if (done) return; done = true; clearTimeout(timer); reject(err); },
        opts,
      );
    });

    try {
      let pos;
      try {
        pos = await tryGetPosition({ enableHighAccuracy: false, maximumAge: 300000, timeout: 15000, _customTimeout: 8000 });
      } catch {
        try {
          pos = await tryGetPosition({ enableHighAccuracy: true, maximumAge: 0, timeout: 30000, _customTimeout: 20000 });
        } catch {
          // IP fallback
          try {
            const ipRes = await fetch('https://ipapi.co/json/');
            if (ipRes.ok) {
              const ipData = await ipRes.json();
              if (ipData?.city) {
                const loc = { city: ipData.city, state: ipData.region, isManual: true, ipFallback: true };
                setUserLocation(loc);
                setLocationPermission('approximate');
                localStorage.setItem('venue_location', JSON.stringify(loc));
                setIsRequestingLocation(false);
                return;
              }
            }
          } catch (_) {}
          if (!isAutoRequest) { setLocationError('Could not get location. Enter city manually.'); setShowLocationDialog(true); }
          else fetchVenues();
          setIsRequestingLocation(false);
          return;
        }
      }

      const { latitude, longitude } = pos.coords;
      const loc = { latitude, longitude, city: 'Your Location', isManual: false };
      setUserLocation(loc);
      setLocationPermission('granted');
      localStorage.setItem('venue_location', JSON.stringify(loc));

      // Reverse geocode (non-blocking)
      (async () => {
        try {
          const r = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=10`);
          const d = await r.json();
          if (d.address) {
            loc.city = d.address.city || d.address.town || d.address.village || d.address.county || loc.city;
            if (d.address.state) loc.state = d.address.state;
            setUserLocation({ ...loc });
            localStorage.setItem('venue_location', JSON.stringify(loc));
          }
        } catch (_) {}
      })();
    } finally {
      setIsRequestingLocation(false);
      if (isAutoRequest) setHasTriedAutoLocation(true);
    }
  };

  const handleManualLocation = () => {
    const city = manualCity.trim();
    if (city.length < 2) { setError('Please enter a valid city name'); return; }
    const loc = { city, isManual: true };
    setUserLocation(loc);
    setShowLocationDialog(false);
    setLocationPermission('manual');
    localStorage.setItem('venue_location', JSON.stringify(loc));
    setManualCity('');
    setError(null);
  };

  const resetLocation = () => {
    setUserLocation(null);
    setLocationPermission('prompt');
    setHasTriedAutoLocation(false);
    localStorage.removeItem('venue_location');
  };

  // ── Scroll helpers ─────────────────────────────────────────────────────────

  const scrollLeft = () => scrollContainerRef.current?.scrollBy({ left: -300, behavior: 'smooth' });
  const scrollRight = () => scrollContainerRef.current?.scrollBy({ left: 300, behavior: 'smooth' });

  // ── Effects ────────────────────────────────────────────────────────────────

  useEffect(() => {
    const saved = localStorage.getItem('venue_location');
    if (saved) { try { setUserLocation(JSON.parse(saved)); setHasTriedAutoLocation(true); } catch (_) {} }
  }, []);

  useEffect(() => { fetchVenues(); }, [userLocation]);

  useEffect(() => {
    const onScroll = () => setShowArrow(window.scrollY > 50);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    let t;
    const handler = () => {
      setShowScrollButtons(true);
      clearTimeout(t);
      t = setTimeout(() => setShowScrollButtons(false), 3000);
    };
    container.addEventListener('scroll', handler);
    return () => { container.removeEventListener('scroll', handler); clearTimeout(t); };
  }, []);

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <section style={{ background: '#0f172a', padding: '56px 0 40px', overflowX: 'hidden' }}>

      {/* ── Section Header ── */}
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 24px 24px', textAlign: 'center' }}>

        <h2 style={{ fontFamily: 'Gilroy-semiBold', fontWeight: 700, fontSize: 'clamp(1.3rem, 3.5vw, 2rem)', color: '#fff', margin: 0, lineHeight: 1.25, display: 'flex', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap', gap: 8 }}>
          {userLocation ? (
            <>
              Venues near{' '}
              <span style={{ color: '#818cf8' }}>
                {userLocation.city}{userLocation.state ? `, ${userLocation.state.slice(0, 2).toUpperCase()}` : ''}
              </span>
              <button onClick={() => setShowLocationDialog(true)} title="Change location"
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#818cf8', padding: '2px 4px', display: 'inline-flex', alignItems: 'center' }}>
                <Edit3 size={17} />
              </button>
            </>
          ) : 'Venues / Studios'}
        </h2>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 'clamp(6px, 2vw, 10px)', marginTop: 12, flexWrap: 'wrap' }}>
          {!userLocation ? (
            <>
              <button onClick={() => requestLocationPermission(false)} disabled={isRequestingLocation}
                style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(129,140,248,0.12)', border: '1px solid rgba(129,140,248,0.3)', color: '#818cf8', borderRadius: 8, padding: 'clamp(6px, 2vw, 10px) clamp(10px, 3vw, 14px)', fontFamily: 'Gilroy-semiBold', fontSize: 'clamp(0.7rem, 2.5vw, 0.82rem)', cursor: 'pointer', transition: 'background 0.2s', whiteSpace: 'nowrap' }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(129,140,248,0.22)'}
                onMouseLeave={e => e.currentTarget.style.background = 'rgba(129,140,248,0.12)'}>
                {isRequestingLocation ? <><Loader size={13} className="animate-spin" /> Locating…</> : <><MapPin size={13} /> Use my location</>}
              </button>
              <button onClick={() => setShowLocationDialog(true)}
                style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'transparent', border: '1px solid rgba(255,255,255,0.15)', color: '#94a3b8', borderRadius: 8, padding: 'clamp(6px, 2vw, 10px) clamp(10px, 3vw, 14px)', fontFamily: 'Gilroy-semiBold', fontSize: 'clamp(0.7rem, 2.5vw, 0.82rem)', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                Enter city
              </button>
            </>
          ) : (
            <button onClick={resetLocation}
              style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontFamily: 'Poppins, sans-serif', fontSize: 'clamp(0.65rem, 2vw, 0.78rem)', textDecoration: 'underline', padding: 0 }}>
              Clear location
            </button>
          )}
        </div>
      </div>

      {/* ── Loading ── */}
      {isLoading && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '56px 0' }}>
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-400" />
        </div>
      )}

      {/* ── Error ── */}
      {locationError && !isLoading && (
        <div style={{ maxWidth: 400, margin: '0 auto 16px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 10, padding: '10px 16px', color: '#fca5a5', fontFamily: 'Poppins, sans-serif', fontSize: '0.85rem', textAlign: 'center' }}>
          {locationError}
        </div>
      )}

      {/* ── Empty ── */}
      {!isLoading && venues.length === 0 && (
        <div style={{ maxWidth: 400, margin: '0 auto 16px', background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.2)', borderRadius: 10, padding: '10px 16px', color: '#a5b4fc', fontFamily: 'Poppins, sans-serif', fontSize: '0.85rem', textAlign: 'center' }}>
          No venues found{userLocation ? ` near ${userLocation.city}` : ''}. Try a different location.
        </div>
      )}

      {/* ── Carousel ── */}
      {venues.length > 0 && (
        <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 24px' }}>
          <div style={{ position: 'relative', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 16, overflow: 'hidden' }} className="group">

            <button onClick={scrollLeft}
              className={`absolute left-3 top-1/2 -translate-y-1/2 z-20 rounded-full w-9 h-9 flex items-center justify-center transition-all duration-300 ${showScrollButtons ? 'opacity-100' : 'opacity-100 md:opacity-0 md:group-hover:opacity-100'}`}
              style={{ background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.2)', color: '#fff', cursor: 'pointer' }}>
              <ChevronLeft size={18} />
            </button>

            <div ref={scrollContainerRef} className="scrollbar-hide"
              style={{ display: 'flex', overflowX: 'auto', gap: 16, padding: '16px 12px', scrollSnapType: 'x mandatory', WebkitOverflowScrolling: 'touch', justifyContent: 'safe center' }}>
              {venues.map((venue) => (
                <div key={venue._id} style={{ flexShrink: 0, width: 256, scrollSnapAlign: 'start' }}>
                  <VenueCard venue={venue} navigate={navigate} />
                </div>
              ))}
            </div>

            <button onClick={scrollRight}
              className={`absolute right-3 top-1/2 -translate-y-1/2 z-20 rounded-full w-9 h-9 flex items-center justify-center transition-all duration-300 ${showScrollButtons ? 'opacity-100' : 'opacity-100 md:opacity-0 md:group-hover:opacity-100'}`}
              style={{ background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.2)', color: '#fff', cursor: 'pointer' }}>
              <ChevronRight size={18} />
            </button>
          </div>

          {/* See all venues — centered below carousel */}
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 32, paddingBottom: 24 }}>
            <button onClick={() => navigate('/all-venues')}
              style={{ background: 'none', border: 'none', color: '#818cf8', fontFamily: 'Gilroy-semiBold', fontSize: 'clamp(0.9rem, 3vw, 1rem)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap', padding: '8px 16px', transition: 'all 0.2s' }}
              onMouseEnter={e => { e.currentTarget.style.textDecoration = 'underline'; e.currentTarget.style.color = '#a5b4fc'; }}
              onMouseLeave={e => { e.currentTarget.style.textDecoration = 'none'; e.currentTarget.style.color = '#818cf8'; }}>
              See all venues <ChevronRight size={18} />
            </button>
          </div>
        </div>
      )}

      {/* ── Location Dialog ── */}
      {showLocationDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div style={{ background: 'linear-gradient(135deg, #1E1B4B 0%, #0F0F23 100%)', borderRadius: 16, padding: '28px', width: '100%', maxWidth: 400, boxShadow: '0 24px 64px rgba(0,0,0,0.5)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ fontFamily: 'Gilroy-semiBold', color: '#e2e8f0', fontSize: '1.15rem', margin: 0 }}>Find venues near you</h3>
              <button onClick={() => { setShowLocationDialog(false); setManualCity(''); }} style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                <X size={22} />
              </button>
            </div>
            <p style={{ color: '#94a3b8', fontFamily: 'Poppins, sans-serif', fontSize: '0.85rem', marginBottom: 16 }}>
              Enter your city to discover local studios and rehearsal spaces.
            </p>
            <input type="text" value={manualCity}
              onChange={e => { setManualCity(e.target.value); if (error) setError(null); }}
              onKeyDown={e => e.key === 'Enter' && handleManualLocation()}
              placeholder="e.g. Mumbai, Delhi, Bangalore"
              autoFocus
              style={{ width: '100%', boxSizing: 'border-box', padding: '10px 14px', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 10, background: 'rgba(255,255,255,0.06)', color: '#fff', fontFamily: 'Poppins, sans-serif', fontSize: '0.95rem', outline: 'none', marginBottom: 16 }} />
            {error && <div style={{ color: '#fca5a5', fontSize: '0.82rem', marginBottom: 8 }}>{error}</div>}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setShowLocationDialog(false)}
                style={{ background: 'none', border: '1px solid rgba(255,255,255,0.15)', color: '#9ca3af', borderRadius: 8, padding: '8px 18px', fontFamily: 'Gilroy-semiBold', cursor: 'pointer' }}>
                Cancel
              </button>
              <button onClick={handleManualLocation} disabled={manualCity.trim().length < 2}
                style={{ background: manualCity.trim().length < 2 ? 'rgba(55,78,169,0.3)' : '#374EA9', border: 'none', color: '#fff', borderRadius: 8, padding: '8px 18px', fontFamily: 'Gilroy-semiBold', cursor: manualCity.trim().length < 2 ? 'not-allowed' : 'pointer', transition: 'background 0.2s' }}>
                Find Venues
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Scroll-to-top */}
      {showArrow && (
        <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-8 right-8 flex items-center justify-center rounded-full w-12 h-12 bg-blue-500 text-white shadow-lg hover:shadow-xl hover:scale-110 active:scale-95 transition-all duration-200 z-50 border-2 border-white/20 backdrop-blur-sm">
          <ArrowUp size={20} />
        </button>
      )}
    </section>
  );
};

export default FeaturedVenues;
